'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

export interface CartItem {
  article_id: string;
  quantity: number;
  title: string;
  price: number;
  image_url: string | null;
  shop_id: string;
}

interface CartState {
  items: CartItem[];
}

function getStorageKey(shopId: string): string {
  return `ballee-cart-${shopId}`;
}

function loadFromStorage(shopId: string): CartItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(getStorageKey(shopId));
    if (!raw) return [];
    return JSON.parse(raw) as CartItem[];
  } catch {
    return [];
  }
}

function saveToStorage(shopId: string, items: CartItem[]): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(getStorageKey(shopId), JSON.stringify(items));
  } catch {
    // ignore storage errors
  }
}

export function useCart(shopId: string) {
  const [state, setState] = useState<CartState>(() => ({
    items: loadFromStorage(shopId),
  }));

  // Tracks the shopId whose cart is currently loaded into state. The lazy
  // initializer above only runs on first mount, so when shopId changes we must
  // reload — and must NOT persist the previous shop's items under the new key.
  const loadedShopId = useRef(shopId);

  // Reload items from storage when shopId changes (the lazy initializer does
  // not re-run on prop change).
  useEffect(() => {
    if (loadedShopId.current === shopId) return;
    loadedShopId.current = shopId;
    setState({ items: loadFromStorage(shopId) });
  }, [shopId]);

  // Persist to localStorage whenever items change — but only once state has
  // been reloaded for the current shopId, so the old cart never overwrites the
  // new shop's storage key.
  useEffect(() => {
    if (loadedShopId.current !== shopId) return;
    saveToStorage(shopId, state.items);
  }, [shopId, state.items]);

  const addItem = useCallback((item: CartItem) => {
    setState((prev) => {
      const existing = prev.items.find((i) => i.article_id === item.article_id);
      if (existing) {
        return {
          items: prev.items.map((i) =>
            i.article_id === item.article_id
              ? { ...i, quantity: i.quantity + item.quantity }
              : i,
          ),
        };
      }
      return { items: [...prev.items, item] };
    });
  }, []);

  const removeItem = useCallback((articleId: string) => {
    setState((prev) => ({
      items: prev.items.filter((i) => i.article_id !== articleId),
    }));
  }, []);

  const updateQuantity = useCallback((articleId: string, quantity: number) => {
    if (quantity <= 0) {
      setState((prev) => ({
        items: prev.items.filter((i) => i.article_id !== articleId),
      }));
      return;
    }
    setState((prev) => ({
      items: prev.items.map((i) =>
        i.article_id === articleId ? { ...i, quantity } : i,
      ),
    }));
  }, []);

  const clearCart = useCallback(() => {
    setState({ items: [] });
  }, []);

  const totalItems = state.items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = state.items.reduce(
    (sum, i) => sum + i.price * i.quantity,
    0,
  );

  return {
    items: state.items,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    totalItems,
    totalPrice,
  };
}
